import { getStrategySubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import type {
  StrategyFilter,
  StrategyFilterOption,
  StrategyFilterSelection,
  StrategyTags
} from './strategy_types'

export const GRADE_OPTIONS: StrategyFilterOption[] = [
  { label: 'Pre-K', value: ['Pre-K'] },
  { label: 'K-2', value: ['K', '1', '2'] },
  { label: '3-5', value: ['3', '4', '5'] },
  { label: '6-8', value: ['6', '7', '8'] },
  { label: '9-12', value: ['9', '10', '11', '12'] }
]

export const EMPTY_TEXT = {
  subject: '(all subjects)',
  barrier: (subjectLabel: string) => (subjectLabel ? `(all barriers for ${subjectLabel})` : '(all barriers)'),
  grades: '(all grades)',
  setting: '(all settings)',
  teachingType: '(all types)'
}

export const EMPTY_SELECTION: StrategyFilterSelection = {
  subject: '',
  barrier: '',
  grades: null,
  setting: '',
  teachingType: '',
  query: ''
}

// A grade selection is only ever one of the fixed bands, so an unrecognised
// grades parameter is no selection rather than a selection with no label: it would
// otherwise filter the search while the trigger read "(all grades)" and offered no
// way to clear itself, and selectionToQuery would keep round-tripping it.
function gradeBandFromQuery(raw: string): string[] | null {
  const parsed = raw.split(',')
  const band = GRADE_OPTIONS.find((option) => (option.value as string[]).join() === parsed.join())
  return (band?.value as string[]) ?? null
}

export function selectionFromQuery(query: Record<string, unknown>): StrategyFilterSelection {
  const grades = typeof query.grades === 'string' ? gradeBandFromQuery(query.grades) : null
  return {
    subject: stringParam(query.subject),
    barrier: stringParam(query.barrier),
    grades,
    setting: stringParam(query.setting),
    teachingType: stringParam(query.teachingType),
    query: stringParam(query.query)
  }
}

// currentQuery carries the ?beta=true opt-in across an in-page navigation. The Ruby
// route fork reads that parameter on a full page load, and router.push replaces the
// whole query rather than merging it, so leaving it out means a reload or a shared
// link serves the Vue 2 page instead of the one the reviewer was looking at.
export function selectionToQuery(
  selection: StrategyFilterSelection,
  currentQuery: Record<string, unknown> = {}
) {
  return {
    beta: typeof currentQuery.beta === 'string' ? currentQuery.beta : undefined,
    subject: selection.subject || undefined,
    barrier: selection.barrier || undefined,
    grades: selection.grades?.join(',') || undefined,
    setting: selection.setting || undefined,
    teachingType: selection.teachingType || undefined,
    query: selection.query || undefined
  }
}

export function gradeLabel(grades: string[] | null) {
  const selected = GRADE_OPTIONS.find((option) => (option.value as string[]).join() === grades?.join())
  return selected?.label || ''
}

export function optionsFromValues(values: string[] = []): StrategyFilterOption[] {
  return values.map((value) => ({ label: value, value }))
}

export function subjectOptions(tags: StrategyTags | null): StrategyFilterOption[] {
  return (tags?.subjects || []).map((subject) => ({
    label: getStrategySubjectLabelFromValue(subject),
    value: subject
  }))
}

export function buildFilters(selection: StrategyFilterSelection, tags: StrategyTags | null): StrategyFilter[] {
  const subjects = subjectOptions(tags)
  const barriers = optionsFromValues(tags?.[selection.subject])

  return [
    {
      label: 'Subject',
      field: 'subject',
      valueLabel: getStrategySubjectLabelFromValue(selection.subject),
      emptyText: EMPTY_TEXT.subject,
      options: subjects,
      disabled: subjects.length === 0
    },
    {
      label: 'Barrier',
      field: 'barrier',
      valueLabel: selection.barrier,
      emptyText: EMPTY_TEXT.barrier(getStrategySubjectLabelFromValue(selection.subject)),
      options: barriers,
      disabled: !selection.subject || barriers.length === 0
    },
    {
      label: 'Grades',
      field: 'grades',
      valueLabel: gradeLabel(selection.grades),
      emptyText: EMPTY_TEXT.grades,
      options: GRADE_OPTIONS,
      disabled: false
    },
    {
      label: 'Type of Instruction',
      field: 'teachingType',
      valueLabel: selection.teachingType,
      emptyText: EMPTY_TEXT.teachingType,
      options: optionsFromValues(tags?.teaching_types),
      disabled: !tags?.teaching_types?.length
    },
    {
      label: 'Instructional Setting',
      field: 'setting',
      valueLabel: selection.setting,
      emptyText: EMPTY_TEXT.setting,
      options: optionsFromValues(tags?.instructional_settings),
      disabled: !tags?.instructional_settings?.length
    }
  ]
}

function stringParam(value: unknown) {
  return typeof value === 'string' ? value : ''
}
